import React from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FormSpy, useForm, useFormState } from 'react-final-form';
import Form from 'components/Form/Layout';
import Select from 'components/Form/FormSelect';
import validateForm from 'utils/Validator';
import { yupCreateProcess } from 'utils/yupFields';
import {
  AdminCreateProcessForm,
  PROCESS_OPERATION_TYPE_OPTIONS,
  ProcessOperationType,
  REPARTO_CAPITAL_SUBTYPE_OPTIONS,
} from 'types/Process';
import { PromotionSummary } from 'types/Project';

const EMPTY_STEP1: AdminCreateProcessForm = {
  operationType: '',
  promotionId: '',
  repartoCapitalSubtype: '',
};

function Step1FormSpy( {
  onValuesChange,
}: {
  onValuesChange?: ( values: AdminCreateProcessForm ) => void,
} ) {
  const { change } = useForm();
  return (
    <FormSpy
      subscription={{ values: true }}
      onChange={( { values } ) => {
        onValuesChange?.( values as AdminCreateProcessForm );
        const v = values as AdminCreateProcessForm;
        const clearSubtype = v.operationType !== ProcessOperationType.REPARTO_CAPITAL
          && v.repartoCapitalSubtype;
        if ( clearSubtype ) {
          change( 'repartoCapitalSubtype', '' );
        }
      }}
    />
  );
}

function RepartoCapitalSubtypeField() {
  const { values } = useFormState( { subscription: { values: true } } );
  const operationType = ( values as AdminCreateProcessForm )?.operationType;
  if ( operationType !== ProcessOperationType.REPARTO_CAPITAL ) return null;
  return (
    <Select
      options={REPARTO_CAPITAL_SUBTYPE_OPTIONS}
      field="repartoCapitalSubtype"
      idKey="value"
      labelKey="labelKey"
      translateOptions
      translateLabel
      label="admin.processes.repartoCapitalSubtypeLabel"
    />
  );
}

type Step1FormProps = {
  activeProjects: PromotionSummary[] | undefined,
  onSubmit: ( values: AdminCreateProcessForm ) => void,
  initialValues?: Partial<AdminCreateProcessForm>,
  onValuesChange?: ( values: AdminCreateProcessForm ) => void,
};

function Step1Form( {
  activeProjects,
  onSubmit,
  initialValues,
  onValuesChange,
}: Step1FormProps ) {
  const { t } = useTranslation();
  const validate = validateForm( yupCreateProcess );

  return (
    <Form
      validate={validate}
      initialValues={{ ...EMPTY_STEP1, ...initialValues }}
      onSubmit={( values ) => onSubmit( values as AdminCreateProcessForm )}
      body={() => (
        <>
          <Step1FormSpy onValuesChange={onValuesChange} />
          <Select
            options={PROCESS_OPERATION_TYPE_OPTIONS}
            field="operationType"
            idKey="value"
            labelKey="labelKey"
            translateOptions
            translateLabel
            label="admin.processes.operationType"
          />
          <RepartoCapitalSubtypeField />
          <Select
            options={activeProjects ?? []}
            field="promotionId"
            idKey="promocionId"
            labelKey="codigoPromocion"
            label="admin.processes.promotion"
            isLoading={activeProjects === undefined}
          />
          <Button
            variant="primary"
            type="submit"
            className="btn--full-width mt-4"
          >
            {t( 'admin.common.create' )}
          </Button>
        </>
      )}
    />
  );
}

export default Step1Form;
